import type { MerchantIdentity } from '../../api/merchant'
import { formatCents } from '../../api/parseMoney'
import type { Account } from '../accounts/useAccounts'
import type { Txn } from './transactionWire'

/** Reads a `YYYY-MM-DD` as a calendar date: UTC midnight, formatted in UTC, so no local offset moves it. */
function calendarDate(date: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!m) throw new Error(`txnDisplay: not a YYYY-MM-DD date: ${JSON.stringify(date)}`)
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
}

/** "Jul 28" — the row's date. No time (Q17). */
export function shortDate(date: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(calendarDate(date))
}

/** "Tuesday, 28 July 2026" — the detail header's date. No time (Q17). */
export function longDate(date: string): string {
  const d = calendarDate(date)
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone: 'UTC' }).format(d)
  const rest = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d)
  return `${weekday}, ${rest}`
}

/**
 * "July" for `2026-07`, or "all time". Given `today`, a month outside today's
 * year carries its year ("July 2025"): the month select spans two years, so a
 * bare "July" would not say which one is empty.
 */
export function monthName(month: string | null, today?: Date): string {
  if (month === null) return 'all time'
  const showYear = today !== undefined && month.slice(0, 4) !== String(today.getFullYear())
  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    ...(showYear ? { year: 'numeric' as const } : {}),
    timeZone: 'UTC',
  }).format(calendarDate(`${month}-01`))
}

/** Is `month` (`YYYY-MM`) before the month `today` falls in? A closed month never fills up again. */
export function isPastMonth(month: string, today: Date): boolean {
  const current = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  return month < current
}

/** "Chequing ••4821", or just the name for an account with no mask. */
export function accountLabel(a: Pick<Account, 'name' | 'mask'>): string {
  return a.mask ? `${a.name} ••${a.mask}` : a.name
}

/**
 * What `MerchantAvatar` draws. A row with no linked merchant gets an identity
 * built from its display name, which renders the initial.
 */
export function avatarOf(t: Txn): MerchantIdentity {
  return t.merchantBrief ?? { id: t.merchantId ?? t.id, name: t.merchant, type: 'other', is_global: false }
}

/**
 * A row's name for assistive technology: merchant, date and amount as plain
 * text. Not currency formatting — that is `Money`'s alone — so the amount is
 * the signed figure and the ISO code, which a screen reader reads unambiguously.
 */
export function rowSummary(t: Txn): string {
  return `${t.merchant}, ${shortDate(t.date)}, ${formatCents(t.amountCents)} ${t.currency}`
}
